import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import {
  RefreshCw, Loader2, Calendar as CalendarIcon, TrendingUp, Users, ShieldCheck, Wallet,
  PieChart as PieIcon, BarChart3, Trophy, Stethoscope, Download, X, ChevronDown, MessageSquare,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, BarChart, Bar, ComposedChart, ReferenceLine,
} from "recharts";
import PageHeader from "@/components/PageHeader";
import PatientSearchCard from "@/components/PatientSearchCard";
import PatientValueCard from "@/components/PatientValueCard";
import PipelineTrendChart from "@/components/PipelineTrendChart";
import ManualAdjustmentsPanel from "@/components/ManualAdjustmentsPanel";
import { useAuth } from "@/contexts/AuthContext";
import CsvExportPopover from "@/components/CsvExportPopover";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { exportCSV } from "@/lib/csv";
import { cachedGet } from "@/lib/apiCache";

const API = window.location.origin + "/api";

const DOCTOR_COLORS = [
  "#3B42DD", "#10b981", "#f59e0b", "#ec4899", "#06b6d4", "#a855f7",
  "#ef4444", "#84cc16", "#14b8a6", "#f97316", "#0ea5e9", "#6366f1",
];

// Chiavi NON mediche escluse dalle linee del grafico e dal calcolo del
// "Totale" (eventi senza medico assegnato + rettifiche manuali virtuali).
const NON_DOCTOR_KEYS = ["—", "Rettifica manuale"];

const PIE_COLORS = [
  "#3B42DD", "#10b981", "#f59e0b", "#ec4899", "#06b6d4", "#a855f7",
  "#ef4444", "#84cc16", "#14b8a6", "#f97316", "#0ea5e9", "#6366f1",
  "#8b5cf6", "#22c55e",
];

const fmtEuro = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const fmtDay = (iso) => {
  if (!iso || iso.length < 10) return iso || "";
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
};

const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const monthOfDate = (iso) => (iso || todayLocalISO()).slice(0, 7);

function DeltaBadge({ delta, label = "vs mese prec." }) {
  if (!delta || delta.pct === null || delta.pct === undefined) {
    return <span className="text-[10px] opacity-50">— {label}</span>;
  }
  const up = delta.pct > 0;
  const flat = delta.pct === 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${
      flat ? "text-slate-400" : up ? "text-emerald-600" : "text-rose-600"
    }`}>
      {flat ? "→" : up ? "▲" : "▼"} {Math.abs(delta.pct)}% {label}
    </span>
  );
}

// Doppio badge (mese-prec + anno-prec) — usato solo sul box "Appuntamenti
// totali" perché è l'unica statistica per cui abbiamo dati storici
// affidabili anno su anno.
function DeltaBadgePair({ delta, yoyDelta }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
      <DeltaBadge delta={delta} label="vs mese prec." />
      <DeltaBadge delta={yoyDelta} label="vs anno prec." />
    </span>
  );
}

function TopDaysCard({ title, icon: Icon, accent, barColor, data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const fmtFullDay = (iso) => {
    if (!iso || iso.length < 10) return iso;
    const d = new Date(iso + "T00:00:00");
    const weekday = d.toLocaleDateString("it-IT", { weekday: "short" });
    return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
      <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${accent}`} /> {title}
      </h2>
      {data.length === 0 ? (
        <div className="py-8 text-center text-slate-400 text-sm">Nessun dato</div>
      ) : (
        <div className="space-y-1.5">
          {data.map((row, i) => {
            const widthPct = Math.round((row.count / max) * 100);
            return (
              <div key={row.date} className="flex items-center gap-2 text-xs" data-testid={`top-day-${i}`}>
                <span className="w-5 text-right text-slate-400 tabular-nums font-semibold">{i + 1}.</span>
                <span className="w-44 truncate text-slate-700 font-medium">{fmtFullDay(row.date)}</span>
                <div className="flex-1 h-5 bg-slate-100 rounded-md overflow-hidden">
                  <div className={`h-full ${barColor} transition-all`} style={{ width: `${widthPct}%` }} />
                </div>
                <span className="w-9 text-right text-slate-800 font-bold tabular-nums">{row.count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, tone = "indigo", delta, yoyDelta, onClick }) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
  };
  const clickable = typeof onClick === "function";
  const Comp = clickable ? "button" : "div";
  return (
    <Comp
      type={clickable ? "button" : undefined}
      onClick={onClick}
      className={`rounded-2xl border p-4 flex items-center gap-3 text-left w-full ${tones[tone]} ${
        clickable ? "hover:shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all cursor-pointer" : ""
      }`}
    >
      <div className="w-11 h-11 rounded-xl bg-white/80 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
        <div className="text-2xl font-bold leading-tight">{value}</div>
        {sub && <div className="text-[11px] opacity-70">{sub}</div>}
        {(delta || yoyDelta) && (
          <div className="mt-0.5">
            {yoyDelta !== undefined
              ? <DeltaBadgePair delta={delta} yoyDelta={yoyDelta} />
              : <DeltaBadge delta={delta} />}
          </div>
        )}
      </div>
    </Comp>
  );
}

// ─── Dialog: dettaglio assicurati del giorno ───
//
// Aperto cliccando sulla card "Assicurati - Solventi" della Dashboard.
// Mostra l'elenco dei pazienti contati come assicurati per la data
// selezionata, con pipeline + stage attuale dell'opp. Include un bottone
// per scaricare in CSV l'intero mese.
function PipelineBadge({ pipeline }) {
  const pretty = {
    clinico: { label: "Clinico", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    orto: { label: "Ortodonzia", cls: "bg-sky-100 text-sky-700 border-sky-200" },
    indiretto_clinico: { label: "Indiretto · Clinico", cls: "bg-amber-100 text-amber-700 border-amber-200" },
    indiretto_orto: { label: "Indiretto · Orto", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  }[pipeline] || { label: pipeline || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border ${pretty.cls}`}>
      {pretty.label}
    </span>
  );
}

function AssicuratiDetailDialog({ open, onOpenChange, date, month }) {
  const [loading, setLoading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ clinico: 0, orto: 0, indiretto_clinico: 0, indiretto_orto: 0 });

  useEffect(() => {
    if (!open || !date) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/dashboard/debug-assicurati`, {
          params: { date },
          withCredentials: true,
        });
        if (cancelled) return;
        const insured = (data?.appointments || []).filter((a) => a.assicurato);
        setRows(insured);
        setCounts(data?.by_pipeline || { clinico: 0, orto: 0, indiretto_clinico: 0, indiretto_orto: 0 });
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.response?.data?.detail || "Errore caricamento elenco assicurati");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, date]);

  const downloadMonthCsv = async () => {
    setDownloadingCsv(true);
    try {
      const resp = await axios.get(`${API}/dashboard/assicurati-export`, {
        params: { month },
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assicurati-${month}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`CSV scaricato — mese ${month}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore download CSV");
    } finally {
      setDownloadingCsv(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[90vw] max-h-[85vh] flex flex-col" data-testid="assicurati-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-amber-600" />
            Assicurati del giorno · {date}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 pt-2">
            <span className="text-slate-600">Totali per pipeline:</span>
            <PipelineBadge pipeline="clinico" /> <strong>{counts.clinico || 0}</strong>
            <PipelineBadge pipeline="orto" /> <strong>{counts.orto || 0}</strong>
            <PipelineBadge pipeline="indiretto_clinico" /> <strong>{counts.indiretto_clinico || 0}</strong>
            <PipelineBadge pipeline="indiretto_orto" /> <strong>{counts.indiretto_orto || 0}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="py-10 text-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Caricamento…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-slate-400 text-sm">
              Nessun assicurato per questa data.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-[10px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-3 py-2">Paziente</th>
                  <th className="text-left px-3 py-2">Pipeline / Stage</th>
                  <th className="text-left px-3 py-2">Medico</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  // Usa i tag_details multi-pipeline se disponibili; fallback
                  // sul singolo pipeline/stage_name (retrocompat).
                  const details = (r.tag_details && r.tag_details.length > 0)
                    ? r.tag_details
                    : (r.pipeline
                      ? [{ pipeline: r.pipeline, stage_name: r.stage_name }]
                      : []);
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`assic-row-${r.id}`}>
                      <td className="px-3 py-2 text-xs font-medium text-slate-700 capitalize align-top">
                        {(r.patient || r.title || "").toLowerCase() || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs align-top">
                        <div className="flex flex-col gap-1.5">
                          {details.length === 0 ? (
                            <span className="text-slate-300">—</span>
                          ) : details.map((d, i) => (
                            <div key={`${d.pipeline}-${i}`} className="flex items-center gap-2">
                              <PipelineBadge pipeline={d.pipeline} />
                              <span className="text-slate-600 truncate" title={d.stage_name || ""}>
                                {d.stage_name || "—"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 align-top">{r.doctor || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div className="text-xs text-slate-500">
            Mostrati <strong>{rows.length}</strong> pazienti assicurati
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={downloadMonthCsv}
              disabled={downloadingCsv}
              data-testid="assicurati-csv-download"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {downloadingCsv
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Download className="w-4 h-4 mr-2" />}
              Scarica CSV mese ({month})
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              <X className="w-4 h-4 mr-1" /> Chiudi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [date, setDate] = useState(todayLocalISO());
  const [month, setMonth] = useState(monthOfDate(todayLocalISO()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [topDays, setTopDays] = useState(null);
  const [showAssicuratiDialog, setShowAssicuratiDialog] = useState(false);
  const intervalRef = useRef(null);
  const retryRef = useRef({ timer: null, attempts: 0 });

  const fetchData = useCallback(async (force = false) => {
    setLoading(true);
    setError("");
    try {
      const params = { date, month };
      if (force) params.force = 1;
      const { data: d } = await axios.get(`${API}/dashboard/overview`, { params });
      setData(d);
      setLastRefresh(new Date());
      retryRef.current.attempts = 0; // reset su successo
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;
      if (status === 503) {
        // GHL momentaneamente non raggiungibile e nessuna cache: messaggio
        // rassicurante + retry automatico (fino a 5 tentativi ogni 6s).
        if (retryRef.current.attempts < 5) {
          retryRef.current.attempts += 1;
          setError(
            `Il gestionale esterno (GHL) è momentaneamente irraggiungibile. ` +
            `Riprovo automaticamente… (tentativo ${retryRef.current.attempts}/5)`
          );
          clearTimeout(retryRef.current.timer);
          retryRef.current.timer = setTimeout(() => fetchData(false), 6000);
        } else {
          setError(
            "Il gestionale esterno (GHL) risulta ancora irraggiungibile. " +
            "I dati torneranno appena il servizio sarà ripristinato: usa “Riprova” tra poco."
          );
        }
      } else {
        setError(err?.response?.data?.detail || "Errore nel caricamento");
      }
    } finally {
      setLoading(false);
    }
  }, [date, month]);

  const fetchTopDays = useCallback(async () => {
    try {
      const year = new Date().getFullYear();
      const { data: d } = await axios.get(`${API}/dashboard/top-days`, { params: { year, limit: 10 } });
      setTopDays(d);
    } catch (err) {
      console.error("fetchTopDays:", err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchTopDays();
    if (intervalRef.current) clearInterval(intervalRef.current);
    // Auto-refresh ogni 5 minuti
    intervalRef.current = setInterval(() => { fetchData(); fetchTopDays(); }, 300000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (retryRef.current.timer) clearTimeout(retryRef.current.timer);
    };
  }, [fetchData, fetchTopDays]);

  // When the user picks a different `date`, default `month` to that date's month.
  useEffect(() => {
    setMonth(monthOfDate(date));
  }, [date]);

  // Wrappati in useMemo per stabilità del riferimento: senza memo, ogni
  // render produce un nuovo oggetto/array (per via di `||` fallback) che
  // invaliderebbe i useMemo a valle.
  const daily = useMemo(
    () => data?.daily || { total: 0, assicurati: 0, assicurati_clinico: 0, assicurati_ortodonzia: 0, indiretti: 0, solventi: 0, by_doctor: [] },
    [data]
  );
  const monthSeries = useMemo(() => data?.month_series || [], [data]);
  // Arricchisco la serie con `incasso_totale` per la linea sinuosa
  // sovrapposta (somma giornaliera diretti + indiretti).
  const monthBreakdown = useMemo(() => {
    const src = data?.month_breakdown_series || [];
    return src.map((d) => ({
      ...d,
      incasso_totale: Number(((d.incasso_diretti || 0) + (d.incasso_indiretti || 0)).toFixed(2)),
    }));
  }, [data]);
  const weeklySeries = data?.weekly_series || [];
  // Chiavi NON mediche da escludere sia dalle linee del grafico sia dal
  // calcolo del "Totale": eventi senza medico assegnato ("—") e le
  // rettifiche manuali (aggiunte virtuali). Richiesta utente 13/10/2026:
  // il "Totale" del grafico deve coincidere con la somma delle linee dei
  // medici, non includere rettifiche/eventi non assegnati.
  const doctorList = useMemo(
    () => (data?.doctors || []).filter((d) => !NON_DOCTOR_KEYS.includes(d)),
    [data]
  );
  // Serie del grafico "Andamento appuntamenti": il campo `total` dal
  // backend può includere rettifiche manuali / bucket "—". Qui ricalcolo
  // un `_docTotal` = somma delle sole linee dei medici così la linea
  // "Totale" è sempre coerente col tooltip per-medico.
  const monthSeriesChart = useMemo(
    () =>
      monthSeries.map((d) => {
        const docTotal = doctorList.reduce((acc, doc) => acc + (d[doc] || 0), 0);
        return { ...d, _docTotal: docTotal };
      }),
    [monthSeries, doctorList]
  );
  const insurance = data?.insurance_share || { items: [], total: 0 };
  const indirettiShare = data?.indiretti_share || { items: [], total: 0 };
  const stageRev = data?.stage_revenue || { pipelines: [] };
  const insuranceTrend = useMemo(() => {
    const t = data?.insurance_revenue_trend || [];
    return t.map((p) => ({
      ...p,
      total_count: (p.clinico_count || 0) + (p.orto_count || 0) + (p.indiretti_count || 0),
      label: p.date ? `${p.date.slice(8, 10)}/${p.date.slice(5, 7)}` : p.date,
    }));
  }, [data]);
  const indirettiStageRev = data?.indiretti_stage_revenue || { pipelines: [] };
  const compare = data?.month_compare || {};
  const monthTotal = useMemo(
    () => monthSeries.reduce((acc, d) => acc + (d.total || 0), 0),
    [monthSeries]
  );
  const today = todayLocalISO();
  // Show the vertical "today" marker only when the viewed month is the
  // current one — and only if `today` actually exists in the series.
  const todayMarker = useMemo(() => {
    if (month !== today.slice(0, 7)) return null;
    return monthSeries.some((d) => d.date === today) ? today : null;
  }, [monthSeries, month, today]);

  const _solventiPct = useMemo(() => {
    const tot = daily.total || 1;
    return Math.round((daily.solventi / tot) * 100);
  }, [daily]);

  // Esporta CSV con selettore di periodo (Settimana/Mese). "Tutti" è
  // disabilitato qui perché la dashboard ha una vista mese-centric e
  // l'export "annuale" non è significativo. Per la settimana ritagliamo
  // dal `month_series` corrente o, se l'offset esce dal mese caricato,
  // facciamo una fetch ad hoc del mese che contiene la settimana.
  const handleExportCSV = async (period, offset, label) => {
    try {
      const now = new Date();
      let rangeStart, rangeEnd, fname;
      if (period === "week") {
        const monday = new Date(now);
        const day = monday.getDay() || 7;
        monday.setDate(monday.getDate() - day + 1 + offset * 7);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        rangeStart = monday.toISOString().slice(0, 10);
        rangeEnd = sunday.toISOString().slice(0, 10);
        fname = `dashboard-settimana-${rangeStart}_${rangeEnd}`;
      } else {
        const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        const targetMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        rangeStart = `${targetMonth}-01`;
        const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        rangeEnd = `${targetMonth}-${String(last).padStart(2, "0")}`;
        fname = `dashboard-mese-${targetMonth}`;
      }
      // Fetch del mese che copre il range (con offset settimana possiamo
      // attraversare 2 mesi: per semplicità fetchiamo il mese di INIZIO).
      const monthParam = rangeStart.slice(0, 7);
      const { data: d } = await axios.get(`${API}/dashboard/overview`, {
        params: { month: monthParam },
        withCredentials: true,
      });
      const ms = d?.month_series || [];
      const mb = d?.month_breakdown_series || [];
      const breakByDate = {};
      mb.forEach((x) => { breakByDate[x.date] = x; });
      const rows = ms
        .filter((row) => row.date >= rangeStart && row.date <= rangeEnd)
        .map((row) => {
          const b = breakByDate[row.date] || {};
          return {
            date: row.date,
            total: row.total || 0,
            assicurati_clinico: b.assicurati_clinico || 0,
            assicurati_ortodonzia: b.assicurati_ortodonzia || 0,
            indiretti: b.indiretti || 0,
            solventi: b.solventi || 0,
          };
        });
      if (!rows.length) {
        toast.error("Nessun dato per il periodo selezionato");
        return;
      }
      const columns = [
        { key: "date", label: "Data" },
        { key: "total", label: "Appuntamenti totali" },
        { key: "assicurati_clinico", label: "Assicurati Clinico" },
        { key: "assicurati_ortodonzia", label: "Assicurati Ortodonzia" },
        { key: "indiretti", label: "Indiretti" },
        { key: "solventi", label: "Solventi" },
      ];
      exportCSV(fname, columns, rows);
      toast.success(`Esportati ${rows.length} giorni — ${label}`);
    } catch (e) {
      console.error(e);
      toast.error("Errore durante il download");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 bg-grid-light pt-[calc(7px+3.5rem+13px)] pb-10 px-4 lg:px-[100px]">
      <div className="w-full">
        <PageHeader
          title="Dashboard"
          subtitle="Andamento appuntamenti, distribuzione assicurazioni, incassi previsti"
          actions={
            <div className="flex items-center gap-2">
              <CsvExportPopover
                title="Esporta dashboard in CSV"
                testIdPrefix="dashboard-export"
                onDownload={handleExportCSV}
                disabledPeriods={["all"]}
              />
              <button
                type="button"
                onClick={() => fetchData(true)}
                data-testid="dashboard-refresh"
                title="Aggiorna ora"
                aria-label="Aggiorna"
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors backdrop-blur-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          }
        />
      </div>

      <div className="w-full mb-6 -mt-8 relative z-10">
        {/* Banner "dati non aggiornati" — appare se il backend serve uno
            snapshot stale (GHL down / rate-limited). Uguale al pattern
            di Chiama Paziente. */}
        {data?.stale && (
          <div
            className="mb-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-2.5 text-sm flex items-start gap-2"
            data-testid="dashboard-stale-banner"
          >
            <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <div className="flex-1">
              <div className="font-semibold">Dati non aggiornati</div>
              <div className="text-xs opacity-80">
                GHL non è raggiungibile in questo momento — stai vedendo una copia salvata
                {data.stale_since && (
                  <>
                    {" "}del{" "}
                    {new Date(data.stale_since).toLocaleString("it-IT", {
                      day: "2-digit", month: "2-digit", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </>
                )}. Prova a ricaricare tra qualche minuto.
              </div>
            </div>
            <button
              type="button"
              onClick={() => fetchData(true)}
              className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline flex-shrink-0"
              data-testid="dashboard-stale-retry"
            >
              Riprova
            </button>
          </div>
        )}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <CalendarIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value || todayLocalISO())}
                data-testid="dashboard-date"
                className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white focus:outline-none focus:border-blue-400"
                title="Giorno"
              />
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <CalendarIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value || monthOfDate(todayLocalISO()))}
                data-testid="dashboard-month"
                className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white focus:outline-none focus:border-blue-400"
                title="Mese di analisi"
              />
            </div>
          </div>
          <div className="text-[11px] text-slate-400">
            {lastRefresh && (<>Ultimo aggiornamento: {lastRefresh.toLocaleTimeString("it-IT")} · auto-refresh ogni 5 min</>)}
          </div>
        </div>

        {error && (
          <div
            className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex items-center justify-between gap-3"
            data-testid="dashboard-error-banner"
          >
            <span className="flex items-center gap-2">
              {retryRef.current.attempts > 0 && retryRef.current.attempts < 5 && (
                <span className="inline-block w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              )}
              {error}
            </span>
            <button
              onClick={() => { retryRef.current.attempts = 0; fetchData(true); }}
              className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700"
              data-testid="dashboard-error-retry"
            >
              Riprova
            </button>
          </div>
        )}
      </div>

      <div className="w-full space-y-6">
        {/* Top stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-stats">
          <StatCard icon={Users} label="Appuntamenti totali" value={monthTotal} sub={`${doctorList.length || 0} medici`} tone="indigo" delta={compare?.appts?.delta} yoyDelta={compare?.appts?.yoy_delta} />
          <StatCard icon={TrendingUp} label="Appuntamenti con assicurazione" value={insurance.total} sub={`${insurance.items.length} assicurazioni`} tone="rose" delta={compare?.insurance?.delta} />
          <StatCard icon={Users} label="Appuntamenti del giorno" value={daily.total} sub={daily.by_doctor?.[0]?.doctor ? `Top: ${daily.by_doctor[0].doctor} (${daily.by_doctor[0].count})` : ""} tone="emerald" />
          <StatCard
            icon={ShieldCheck}
            label="Assicurati - Solventi"
            value={`${(daily.assicurati || 0) + (daily.indiretti || 0)} - ${daily.solventi}`}
            sub={`Clinico ${daily.assicurati_clinico} · Orto ${daily.assicurati_ortodonzia}${
              daily.indiretti ? ` · Indiretti ${daily.indiretti}` : ""
            }`}
            tone="amber"
            onClick={() => setShowAssicuratiDialog(true)}
          />
        </div>

        <AssicuratiDetailDialog
          open={showAssicuratiDialog}
          onOpenChange={setShowAssicuratiDialog}
          date={date}
          month={month}
        />

        {/* Andamento mese */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-indigo-500" /> Andamento appuntamenti — {month}
            </h2>
          </div>
          {monthSeries.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">Nessun dato per questo mese</div>
          ) : (
            <div style={{ width: "100%", height: 320 }} data-testid="month-trend-chart">
              <ResponsiveContainer>
                <LineChart data={monthSeriesChart} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(v) => `Giorno ${fmtDay(v)}`}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="_docTotal" name="Totale" stroke="#475569" strokeWidth={2} dot={{ r: 3 }} />
                  {doctorList.map((doc, i) => (
                    <Line key={doc} type="monotone" dataKey={doc} stroke={DOCTOR_COLORS[i % DOCTOR_COLORS.length]} strokeWidth={1.5} dot={false} />
                  ))}
                  {todayMarker && (
                    <ReferenceLine
                      x={todayMarker}
                      stroke="#ef4444"
                      strokeWidth={1.5}
                      strokeDasharray="4 4"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Andamento appuntamenti per mese (12 mesi) — spostato subito dopo "Andamento appuntamenti" */}
        <AppointmentsTrendByMonthCard />

        {/* Andamento settimanale Assicurati vs Solventi */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-purple-500" /> Giornaliero — Totale + breakdown Assicurati/Solventi
            </h2>
            <div className="text-[11px] text-slate-400">{month}</div>
          </div>
          {monthBreakdown.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">Nessun dato per questo mese</div>
          ) : (
            <div style={{ width: "100%", height: 320 }} data-testid="daily-breakdown-chart">
              <ResponsiveContainer>
                <ComposedChart data={monthBreakdown} margin={{ top: 5, right: 50, bottom: 5, left: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis
                    yAxisId="appts"
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    allowDecimals={false}
                  />
                  <YAxis
                    yAxisId="eur"
                    orientation="right"
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    tickFormatter={(v) => `€${Math.round(v / 1000)}k`}
                  />
                  <Tooltip
                    labelFormatter={(v) => `Giorno ${fmtDay(v)}`}
                    formatter={(value, name) =>
                      name === "Incasso indiretti (€)" || name === "Incasso diretti (€)" || name === "Incasso totale (€)"
                        ? [fmtEuro(value), name]
                        : [value, name]
                    }
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="appts" dataKey="assicurati_clinico" name="Assicurati · Clinico" stackId="b" fill="#10b981" />
                  <Bar yAxisId="appts" dataKey="assicurati_ortodonzia" name="Assicurati · Orto" stackId="b" fill="#3b82f6" />
                  <Bar yAxisId="appts" dataKey="indiretti" name="Indiretti" stackId="b" fill="#facc15" />
                  <Bar yAxisId="appts" dataKey="solventi" name="Solventi" stackId="b" fill="#f59e0b" radius={[5, 5, 0, 0]} />
                  {/* Diretti / Indiretti: solo linee dritte sottili, senza pallini. */}
                  <Line
                    yAxisId="eur"
                    type="linear"
                    dataKey="incasso_indiretti"
                    name="Incasso indiretti (€)"
                    stroke="#a855f7"
                    strokeWidth={1}
                    dot={false}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                  <Line
                    yAxisId="eur"
                    type="linear"
                    dataKey="incasso_diretti"
                    name="Incasso diretti (€)"
                    stroke="#0ea5e9"
                    strokeWidth={1}
                    dot={false}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                  {/* Totale (diretti + indiretti) per giorno: SOLO tratto
                      orizzontale nero spesso sul valore, nessuna linea di
                      collegamento. */}
                  <Line
                    yAxisId="eur"
                    type="linear"
                    dataKey="incasso_totale"
                    name="Incasso totale (€)"
                    stroke="transparent"
                    strokeWidth={0}
                    dot={(props) =>
                      props.payload && (props.payload.incasso_totale || 0) > 0
                        ? (
                          <line
                            x1={props.cx - 7}
                            x2={props.cx + 7}
                            y1={props.cy}
                            y2={props.cy}
                            stroke="#0f172a"
                            strokeWidth={4}
                            strokeLinecap="round"
                          />
                        )
                        : null
                    }
                    activeDot={false}
                    isAnimationActive={false}
                    legendType="plainline"
                  />
                  {todayMarker && (
                    <ReferenceLine
                      yAxisId="appts"
                      x={todayMarker}
                      stroke="#ef4444"
                      strokeWidth={1.5}
                      strokeDasharray="4 4"
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Andamento settimanale Assicurati vs Solventi */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-500" /> Andamento settimanale — Assicurati vs Solventi
            </h2>
            <div className="text-[11px] text-slate-400">Settimana ISO</div>
          </div>
          {weeklySeries.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">Nessun dato per questo mese</div>
          ) : (
            <div style={{ width: "100%", height: 280 }} data-testid="weekly-chart">
              <ResponsiveContainer>
                <BarChart data={weeklySeries} margin={{ top: 5, right: 10, bottom: 5, left: 0 }} barCategoryGap="20%">
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="week"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    tickFormatter={(v, i) => {
                      const w = weeklySeries[i];
                      return w ? `${fmtDay(w.start)}–${fmtDay(w.end)}` : v;
                    }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                    labelFormatter={(_v, payload) => {
                      const w = payload?.[0]?.payload;
                      return w ? `${w.week} · ${fmtDay(w.start)}–${fmtDay(w.end)}` : _v;
                    }}
                    formatter={(value, name, ctx) => {
                      const w = ctx?.payload || {};
                      const tot = (w.assicurati_clinico || 0) + (w.assicurati_ortodonzia || 0) + (w.indiretti || 0) + (w.solventi || 0);
                      const pct = tot > 0 ? Math.round((value / tot) * 100) : 0;
                      return [`${value} (${pct}%)`, name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="assicurati_clinico" name="Assicurati · Clinico" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="assicurati_ortodonzia" name="Assicurati · Orto" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="indiretti" name="Indiretti" stackId="a" fill="#facc15" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="solventi" name="Solventi" stackId="a" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Andamento € giornaliero per stage — Pipeline analytics */}
        <PipelineTrendChart />

        {isAdmin && (
          <ManualAdjustmentsPanel onChange={() => fetchData(true)} />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 lg:col-span-2">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3">
              <PieIcon className="w-4 h-4 text-rose-500" /> Distribuzione assicurazioni — {month}
            </h2>
            {insurance.items.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm">Nessun dato assicurazioni per questo mese</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                <div style={{ width: "100%", height: 280 }} data-testid="insurance-donut">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={insurance.items} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}>
                        {insurance.items.map((it, i) => (
                          <Cell key={`pie-${it.name}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                        formatter={(v, n) => [`${v} (${Math.round((v / insurance.total) * 100)}%)`, n]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="max-h-72 overflow-y-auto space-y-1 pr-2">
                  {insurance.items.map((it, i) => (
                    <div key={it.name} className="flex items-center gap-2 text-xs">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="flex-1 truncate text-slate-700 font-medium" title={it.name}>{it.name}</span>
                      <span className="text-slate-500 tabular-nums">{it.value}</span>
                      <span className="text-slate-400 tabular-nums w-10 text-right">{Math.round((it.value / insurance.total) * 100)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Distribuzione Indiretti (clinico vs orto) — donut separato.
              Mostrato solo se ci sono opp indirette nel mese. */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 lg:col-span-1" data-testid="indiretti-donut-card">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3">
              <PieIcon className="w-4 h-4 text-amber-500" /> Indiretti — {month}
            </h2>
            {indirettiShare.items.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm">Nessun paziente indiretto questo mese</div>
            ) : (
              <>
                <div style={{ width: "100%", height: 220 }} data-testid="indiretti-donut">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={indirettiShare.items} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                        {indirettiShare.items.map((it) => (
                          <Cell key={`ind-${it.name}`} fill={it.name === "Reparto Clinico" ? "#10b981" : "#3b82f6"} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                        formatter={(v, n) => [`${v} (${Math.round((v / Math.max(indirettiShare.total, 1)) * 100)}%)`, n]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1">
                  {indirettiShare.items.map((it) => (
                    <div key={it.name} className="flex items-center gap-2 text-xs">
                      <span className="w-3 h-3 rounded-full" style={{ background: it.name === "Reparto Clinico" ? "#10b981" : "#3b82f6" }} />
                      <span className="flex-1 text-slate-700 font-medium">{it.name}</span>
                      <span className="text-slate-500 tabular-nums">{it.value}</span>
                      <span className="text-slate-400 tabular-nums w-10 text-right">{Math.round((it.value / Math.max(indirettiShare.total, 1)) * 100)}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3">
              <Stethoscope className="w-4 h-4 text-emerald-500" /> Giorno selezionato · per medico
            </h2>
            <div style={{ width: "100%", height: 220 }} data-testid="day-donut">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={(daily.by_doctor || []).map((d) => ({ name: d.doctor, value: d.count }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={80}
                  >
                    {(daily.by_doctor || []).map((d, i) => (
                      <Cell key={d.doctor} fill={DOCTOR_COLORS[i % DOCTOR_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(value, name) => {
                      const tot = (daily.by_doctor || []).reduce((acc, d) => acc + (d.count || 0), 0);
                      const pct = tot > 0 ? Math.round((value / tot) * 100) : 0;
                      return [`${value} (${pct}%)`, name];
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5">
              {daily.by_doctor.map((d, i) => (
                <div key={d.doctor} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5 truncate">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: DOCTOR_COLORS[i % DOCTOR_COLORS.length] }} />
                    <span className="truncate text-slate-700">{d.doctor}</span>
                  </span>
                  <span className="font-semibold text-slate-600 tabular-nums">{d.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top 10 days of the year — both totals + assicurati */}
        {topDays && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopDaysCard
              title={`Top 10 giorni — Totale appuntamenti ${topDays.year}`}
              icon={Trophy}
              accent="text-amber-500"
              barColor="bg-indigo-500"
              data={topDays.top_total}
            />
            <TopDaysCard
              title={`Top 10 giorni — Assicurati ${topDays.year}`}
              icon={Trophy}
              accent="text-emerald-500"
              barColor="bg-emerald-500"
              data={topDays.top_assicurati}
            />
          </div>
        )}

        {/* Andamento cumulativo introiti assicurazioni (Clinico + Orto + Totale) */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="insurance-revenue-trend-card">
          <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-1">
            <Wallet className="w-4 h-4 text-indigo-500" /> Andamento introiti assicurazioni — cumulativo dall'inizio
          </h2>
          <p className="text-xs text-slate-400 mb-4">
            Somma progressiva del valore delle opportunità aperte per data di inserimento piano.
          </p>
          {insuranceTrend.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-sm text-slate-400" data-testid="insurance-revenue-trend-empty">
              Nessun dato disponibile
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-4 mb-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">Clinico</div>
                  <div className="text-lg font-bold text-blue-600 tabular-nums">{fmtEuro(insuranceTrend[insuranceTrend.length - 1]?.clinico || 0)}</div>
                  <div className="text-[11px] text-slate-400 tabular-nums">{insuranceTrend[insuranceTrend.length - 1]?.clinico_count || 0} piani</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">Ortodonzia</div>
                  <div className="text-lg font-bold text-fuchsia-600 tabular-nums">{fmtEuro(insuranceTrend[insuranceTrend.length - 1]?.orto || 0)}</div>
                  <div className="text-[11px] text-slate-400 tabular-nums">{insuranceTrend[insuranceTrend.length - 1]?.orto_count || 0} piani</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">Indiretti</div>
                  <div className="text-lg font-bold text-amber-600 tabular-nums">{fmtEuro(insuranceTrend[insuranceTrend.length - 1]?.indiretti || 0)}</div>
                  <div className="text-[11px] text-slate-400 tabular-nums">{insuranceTrend[insuranceTrend.length - 1]?.indiretti_count || 0} piani</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">Totale</div>
                  <div className="text-lg font-bold text-emerald-600 tabular-nums">{fmtEuro(insuranceTrend[insuranceTrend.length - 1]?.total || 0)}</div>
                  <div className="text-[11px] text-slate-400 tabular-nums">{insuranceTrend[insuranceTrend.length - 1]?.total_count || 0} piani</div>
                </div>
              </div>
              <div className="h-72" data-testid="insurance-revenue-trend-chart">
                <ResponsiveContainer>
                  <LineChart data={insuranceTrend} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} minTickGap={24} />
                    <YAxis yAxisId="eur" tickFormatter={(v) => `€${Math.round(v / 1000)}k`} tick={{ fontSize: 10, fill: "#94a3b8" }} width={48} />
                    <YAxis yAxisId="cnt" orientation="right" allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} width={36} />
                    <Tooltip
                      formatter={(v, name) => [
                        (name === "Piani Clinico" || name === "Piani Ortodonzia" || name === "Piani Indiretti" || name === "Piani Totali") ? `${v} piani` : fmtEuro(v),
                        name,
                      ]}
                      labelFormatter={(l) => `Giorno ${l}`}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line yAxisId="eur" type="monotone" dataKey="total" name="Totale" stroke="#10b981" strokeWidth={2.5} dot={false} />
                    <Line yAxisId="eur" type="monotone" dataKey="clinico" name="Reparto Clinico" stroke="#2563eb" strokeWidth={2} dot={false} />
                    <Line yAxisId="eur" type="monotone" dataKey="orto" name="Ortodonzia" stroke="#c026d3" strokeWidth={2} dot={false} />
                    <Line yAxisId="eur" type="monotone" dataKey="indiretti" name="Indiretti" stroke="#d97706" strokeWidth={2} dot={false} />
                    <Line yAxisId="cnt" type="monotone" dataKey="total_count" name="Piani Totali" stroke="#10b981" strokeWidth={2} strokeDasharray="6 4" dot={false} />
                    <Line yAxisId="cnt" type="monotone" dataKey="clinico_count" name="Piani Clinico" stroke="#60a5fa" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
                    <Line yAxisId="cnt" type="monotone" dataKey="orto_count" name="Piani Ortodonzia" stroke="#e879f9" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
                    <Line yAxisId="cnt" type="monotone" dataKey="indiretti_count" name="Piani Indiretti" stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>

        {/* Stage revenue — barre stacked per assicurazione */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
            <Wallet className="w-4 h-4 text-blue-500" /> Introiti previsti per fase pipeline
          </h2>
          {(() => {
            // Costruisco un'unica lista di assicurazioni vista in qualsiasi
            // stage di qualsiasi pipeline, così:
            //   - la legenda è unica (sopra i due grafici).
            //   - ogni assicurazione ha lo STESSO colore in entrambe le
            //     pipeline (mapping deterministico in base all'ordine
            //     alfabetico).
            const insuranceSet = new Set();
            stageRev.pipelines.forEach((p) => {
              p.stages.forEach((s) => {
                Object.keys(s.by_insurance || {}).forEach((k) => insuranceSet.add(k));
              });
            });
            // Ordino le assicurazioni per il numero iniziale presente nel
            // label (es. "2 - In Salute" < "3 - Onenet-Aon" < "20 - Fasi
            // Assidai"). Se manca il numero iniziale (es. "Non
            // specificata") finiscono in coda in ordine alfabetico.
            const leadingNum = (s) => {
              const m = (s || "").match(/^\s*(\d+)/);
              return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
            };
            const allInsurances = Array.from(insuranceSet).sort((a, b) => {
              const na = leadingNum(a);
              const nb = leadingNum(b);
              if (na !== nb) return na - nb;
              return a.localeCompare(b, "it");
            });
            const colorMap = Object.fromEntries(
              allInsurances.map((ins, i) => [ins, DOCTOR_COLORS[i % DOCTOR_COLORS.length]])
            );
            const lastIdx = allInsurances.length - 1;

            return (
              <>
                {/* Legenda unica */}
                <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-4 pb-3 border-b border-slate-100">
                  {allInsurances.map((ins) => (
                    <span key={ins} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                      <span
                        className="w-3 h-3 rounded-sm"
                        style={{ background: colorMap[ins] }}
                      />
                      {ins}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {stageRev.pipelines.map((p) => {
                    const data = p.stages.map((s) => {
                      const row = { name: s.name, _total: s.value };
                      allInsurances.forEach((ins) => {
                        row[ins] = (s.by_insurance || {})[ins] || 0;
                      });
                      return row;
                    });
                    return (
                      <div key={p.id} data-testid={`stage-revenue-${p.id}`}>
                        <div className="flex items-baseline justify-between mb-2">
                          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">{p.name}</h3>
                          <div className="text-sm font-bold text-slate-800">{fmtEuro(p.total)}</div>
                        </div>
                        <div style={{ width: "100%", height: Math.max(240, p.stages.length * 36) }}>
                          <ResponsiveContainer>
                            <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 0 }}>
                              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                              <XAxis type="number" tickFormatter={(v) => fmtEuro(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} width={140} />
                              <Tooltip
                                contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                                formatter={(v, name) => [fmtEuro(v), name]}
                              />
                              {allInsurances.map((ins, idx) => (
                                <Bar
                                  key={ins}
                                  dataKey={ins}
                                  stackId="rev"
                                  fill={colorMap[ins]}
                                  radius={idx === lastIdx ? [0, 6, 6, 0] : [0, 0, 0, 0]}
                                />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>

        {/* Introiti Indiretti — barre stacked per source/reparto.
            Mostrato solo se ci sono opp indirette aperte con valore. */}
        {indirettiStageRev.pipelines.some((p) => p.total > 0) && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="indiretti-stage-revenue-card">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
              <Wallet className="w-4 h-4 text-amber-500" /> Introiti previsti — Indiretti
            </h2>
            {(() => {
              const insuranceSet = new Set();
              indirettiStageRev.pipelines.forEach((p) => {
                p.stages.forEach((s) => {
                  Object.keys(s.by_insurance || {}).forEach((k) => insuranceSet.add(k));
                });
              });
              const allSources = Array.from(insuranceSet).sort((a, b) => a.localeCompare(b, "it"));
              // Palette giallo/ambra distinta per gli Indiretti (richiamo
              // colore della convenzione "giallo = paziente indiretto").
              const INDIRETTI_PALETTE = ["#facc15", "#f59e0b", "#fb923c", "#eab308", "#ca8a04", "#d97706"];
              const colorMap = Object.fromEntries(
                allSources.map((s, i) => [s, INDIRETTI_PALETTE[i % INDIRETTI_PALETTE.length]])
              );
              const lastIdx = allSources.length - 1;
              return (
                <>
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-4 pb-3 border-b border-slate-100">
                    {allSources.map((s) => (
                      <span key={s} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                        <span className="w-3 h-3 rounded-sm" style={{ background: colorMap[s] }} />
                        {s}
                      </span>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {indirettiStageRev.pipelines.map((p) => {
                      // Mostro SOLO i 2 stage centrali attivi (escludo
                      // "New Lead" + "Closed"/finali con valore 0), come
                      // richiesto dall'utente.
                      const activeStages = p.stages.filter((s) => (s.value || 0) > 0 || (s.count || 0) > 0);
                      const data = activeStages.map((s) => {
                        const row = { name: s.name, _total: s.value };
                        allSources.forEach((src) => {
                          row[src] = (s.by_insurance || {})[src] || 0;
                        });
                        return row;
                      });
                      if (data.length === 0) return null;
                      return (
                        <div key={p.id} data-testid={`indiretti-stage-revenue-${p.id}`}>
                          <div className="flex items-baseline justify-between mb-2">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-amber-700">{p.name}</h3>
                            <div className="text-sm font-bold text-slate-800">{fmtEuro(p.total)}</div>
                          </div>
                          <div style={{ width: "100%", height: Math.max(200, data.length * 48) }}>
                            <ResponsiveContainer>
                              <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 0 }}>
                                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                                <XAxis type="number" tickFormatter={(v) => fmtEuro(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} width={140} />
                                <Tooltip
                                  contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                                  formatter={(v, name) => [fmtEuro(v), name]}
                                />
                                {allSources.map((src, idx) => (
                                  <Bar
                                    key={src}
                                    dataKey={src}
                                    stackId="rev"
                                    fill={colorMap[src]}
                                    radius={idx === lastIdx ? [0, 6, 6, 0] : [0, 0, 0, 0]}
                                  />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ─── Sezione: Incassato per Assicurazione (mese selezionato) ─── */}
        <InsuranceRevenueByMonthCard selectedMonth={month} />

        {/* ─── Sezione: Incassi giornalieri pazienti INDIRETTI (mese
              selezionato) — richiesta utente 06/06/2026 ─── */}
        <IndirettiDailyRevenueCard selectedMonth={month} />

        {/* ─── Sezione (nuova 02/06/2026 — richiesta utente):
              1) Quantità prestazioni erogate per codice (bar verticale
                 stacked top10)
              2) Valori dei piani di cura completi (€ + count combo)
              Entrambe con filtri mese/anno/ricerca prestazione.
              
              Ordine (modifica 13/06/2026 — richiesta utente):
              1) Piani di cura · valore + numero (CompletedPlansByMonthCard)
              2) Top 10 prestazioni per assicurazione (TreatmentsByInsuranceCard)
              3) Prestazioni erogate per mese (TreatmentsByMonthCard) */}
        <CompletedPlansByMonthCard />
        {/* Box di ricerca pazienti per filtri composti — inserito qui
            (richiesta utente 25/06/2026) prima di "Prestazioni per
            Assicurazione" per dare un'analisi mirata pre-aggregata. */}
        <PatientSearchCard />
        {/* Box "Valore Opportunità Paziente" — aggregazione per paziente
            con filtri per assicurazione, mesi, stato (open/won) e fasce
            di valore € (richiesta utente 26/06/2026). */}
        <PatientValueCard />
        <TreatmentsByInsuranceCard selectedMonth={month} />
        <TreatmentsByMonthCard />
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Card: Andamento appuntamenti per mese (ultimi 12 mesi)
//
// LineChart con 4 serie: Totale, Assicurati (dir+ind), Solo diretti,
// Solo indiretti. Permette di vedere il trend di lungo periodo.
// ────────────────────────────────────────────────────────────────────
function AppointmentsTrendByMonthCard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: resp } = await axios.get(
          `${API}/dashboard/appointments-trend-by-month`,
          { params: { months }, withCredentials: true },
        );
        if (!cancelled) setData(resp?.rows || []);
      } catch (err) {
        console.debug("appointments-trend:", err?.response?.status);
        if (!cancelled) setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [months]);

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mt-4"
      data-testid="appointments-trend-by-month"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-indigo-500" />
          Andamento appuntamenti per mese
        </h3>
        <select
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
          className="text-xs border border-slate-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-300"
          data-testid="trend-months-select"
        >
          <option value={6}>6 mesi</option>
          <option value={12}>12 mesi</option>
          <option value={18}>18 mesi</option>
          <option value={24}>24 mesi</option>
        </select>
      </div>
      {loading ? (
        <div className="h-72 flex items-center justify-center text-xs text-slate-400">
          Caricamento…
        </div>
      ) : data.length === 0 ? (
        <div className="h-72 flex items-center justify-center text-xs text-slate-400">
          Nessun dato disponibile
        </div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#64748b" }}
                stroke="#cbd5e1"
              />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
                formatter={(value, name) => [value, name]}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Line
                type="monotone"
                dataKey="total"
                name="Totale"
                stroke="#475569"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="assicurati"
                name="Assicurati (dir+ind)"
                stroke="#0ea5e9"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="diretti"
                name="Solo diretti"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="indiretti"
                name="Solo indiretti"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Card: Pazienti ricorrenti (Top Pazienti per continuità di mese)
//
// Mostra i pazienti con appuntamenti in MESI CONSECUTIVI nell'arco
// scelto dalla tendina. Ogni riga ha:
//   - nome paziente
//   - badge "streak" (numero di mesi consecutivi con almeno 1 appt)
//   - totale appuntamenti
//   - mini-sparkline (BarChart) dei conteggi mensili
//
// Sorgente: GET /api/dashboard/patient-frequency?months=N&min_streak=2
// La prima richiesta su finestre lunghe (12+ mesi) può tornare con
// `team_filter_applied: false` (filtro TEAM in warm-up): in quel caso
// mostriamo un banner giallo e ri-fetchamo automaticamente dopo 30s.
// ────────────────────────────────────────────────────────────────────
function PatientFrequencyCard() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const retryTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const { data: resp } = await axios.get(
          `${API}/dashboard/patient-frequency`,
          { params: { months, min_streak: 2 }, withCredentials: true },
        );
        if (cancelled) return;
        setData(resp);
        // Auto-retry se TEAM filter ancora in warm-up
        if (resp && resp.team_filter_applied === false) {
          if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(fetchData, 30000);
        }
      } catch (err) {
        if (cancelled) return;
        console.debug("patient-frequency:", err?.response?.status);
        setError("Impossibile caricare i pazienti ricorrenti");
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [months]);

  const patients = data?.patients || [];
  const monthRanges = data?.month_ranges || [];
  const totalPatients = data?.total_patients || 0;
  const warming = data?.team_filter_applied === false;

  // Map ym → short label "Gen", "Feb", ... per la tabella header sparkline
  const shortMonthLabels = useMemo(() => {
    const MONTH_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
    return monthRanges.map((mr) => {
      const m = parseInt(mr.ym.slice(5, 7), 10);
      return MONTH_IT[m - 1] || mr.ym;
    });
  }, [monthRanges]);

  const streakBadgeColor = (s) => {
    if (s >= 8) return "bg-emerald-100 text-emerald-700 border-emerald-300";
    if (s >= 5) return "bg-sky-100 text-sky-700 border-sky-300";
    if (s >= 3) return "bg-indigo-100 text-indigo-700 border-indigo-300";
    return "bg-slate-100 text-slate-600 border-slate-300";
  };

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mt-4"
      data-testid="patient-frequency-card"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            Pazienti ricorrenti — andamento mese per mese
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Pazienti con appuntamenti in mesi consecutivi (streak ≥ 2)
            {!loading && data && (
              <span className="ml-1 text-slate-400">· {totalPatients} totali</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/pazienti-ricorrenti"
            className="text-[11px] text-indigo-600 hover:text-indigo-800 underline-offset-2 hover:underline"
            data-testid="open-dedicated-page"
            title="Apri pagina dedicata con range da-a e lista completa"
          >
            Apri pagina dedicata →
          </a>
          <select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="text-xs border border-slate-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300"
            data-testid="patient-freq-months-select"
          >
            <option value={3}>3 mesi</option>
            <option value={6}>6 mesi</option>
            <option value={12}>12 mesi</option>
            <option value={24}>24 mesi</option>
            <option value={36}>36 mesi</option>
          </select>
        </div>
      </div>

      {warming && !loading && (
        <div
          className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700 flex items-center gap-2"
          data-testid="patient-freq-warming"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
          Filtro staff in caricamento — la lista potrebbe contenere dottori/staff. Aggiornamento automatico tra 30 secondi.
        </div>
      )}

      {loading ? (
        <div className="h-48 flex items-center justify-center text-xs text-slate-400">
          Caricamento…
        </div>
      ) : error ? (
        <div className="h-48 flex items-center justify-center text-xs text-rose-500">
          {error}
        </div>
      ) : patients.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-xs text-slate-400">
          Nessun paziente trovato con almeno 2 mesi consecutivi
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="patient-frequency-table">
            <thead>
              <tr className="text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 pr-2 font-semibold w-8">#</th>
                <th className="text-left py-2 pr-3 font-semibold">Paziente</th>
                <th className="text-center py-2 px-2 font-semibold w-20">Streak</th>
                <th className="text-center py-2 px-2 font-semibold w-16">Totale</th>
                <th className="text-left py-2 pl-3 font-semibold">
                  Andamento{" "}
                  <span className="text-slate-400 font-normal">
                    ({shortMonthLabels[0]} → {shortMonthLabels[shortMonthLabels.length - 1]})
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {patients.slice(0, 200).map((p, i) => (
                <PatientFrequencyRow
                  key={p.contactId}
                  rank={i + 1}
                  patient={p}
                  monthRanges={monthRanges}
                  streakBadgeColor={streakBadgeColor}
                />
              ))}
            </tbody>
          </table>
          {patients.length > 200 && (
            <div className="mt-2 text-[11px] text-slate-400 text-center">
              Mostrati i primi 200 pazienti (totale {patients.length}).
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Riga della tabella + sparkline mensile.
function PatientFrequencyRow({ rank, patient, monthRanges, streakBadgeColor }) {
  const sparkData = useMemo(
    () => monthRanges.map((mr) => ({
      ym: mr.ym,
      label: mr.ym.slice(5, 7),
      count: patient.monthly?.[mr.ym] || 0,
    })),
    [monthRanges, patient.monthly],
  );

  return (
    <tr
      className="hover:bg-slate-50 transition-colors"
      data-testid={`patient-freq-row-${patient.contactId}`}
    >
      <td className="py-2 pr-2 text-slate-400 tabular-nums">{rank}</td>
      <td className="py-2 pr-3 font-medium text-slate-800 truncate max-w-[280px]">
        {patient.has_feedback && (
          <span
            className="inline-flex items-center gap-1 mr-1.5 px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 border border-emerald-300 text-[9px] font-bold uppercase tracking-wide align-middle"
            title="Ha lasciato feedback"
            data-testid={`feedback-badge-mini-${patient.contactId}`}
          >
            <MessageSquare className="w-2.5 h-2.5" />
            Feedback
          </span>
        )}
        {patient.name}
      </td>
      <td className="py-2 px-2 text-center">
        <span
          className={`inline-block px-2 py-0.5 rounded-md border text-[11px] font-bold tabular-nums ${streakBadgeColor(patient.max_streak)}`}
          title={`Massima sequenza di ${patient.max_streak} mesi consecutivi`}
        >
          {patient.max_streak}
        </span>
      </td>
      <td className="py-2 px-2 text-center font-semibold text-slate-700 tabular-nums">
        {patient.total}
      </td>
      <td className="py-2 pl-3">
        <div style={{ width: "100%", height: 38, minWidth: 180 }}>
          <ResponsiveContainer>
            <BarChart data={sparkData} margin={{ top: 2, right: 2, bottom: 0, left: 0 }}>
              <Tooltip
                cursor={{ fill: "rgba(99,102,241,0.05)" }}
                contentStyle={{ fontSize: 10, borderRadius: 6, border: "1px solid #e2e8f0", padding: "2px 6px" }}
                labelFormatter={(label, payload) => payload?.[0]?.payload?.ym || label}
                formatter={(value) => [value, "appt"]}
              />
              <Bar
                dataKey="count"
                fill="#6366f1"
                radius={[2, 2, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </td>
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────────
// Card: Incassato per Assicurazione (grafico — mese selezionato in Header)
//
// Mostra un grafico a barre orizzontali (per leggibilità dei nomi
// assicurazione lunghi). Per il mese attivo (passato come prop dal Header
// della Dashboard), ogni assicurazione ha una barra stacked con due
// segmenti: Diretto (verde) + Indiretto (ambra).
//
// Sotto il grafico ci sono 3 KPI: Diretto totale, Indiretto totale,
// Totale incassato. Toggle "intero range vs mese" rimosso: la card è
// sempre legata al filtro mese dell'Header (richiesta utente 21/05/2026).
// ────────────────────────────────────────────────────────────────────
function InsuranceRevenueByMonthCard({ selectedMonth }) {
  // Carico ultimi 24 mesi così l'utente può vedere qualsiasi mese
  // selezionato dall'Header senza dover rifare la fetch ogni volta.
  // L'API caching si occupa di renderlo veloce (1 sola chiamata).
  const [data, setData] = useState({ months: [], rows: [] });
  const [loading, setLoading] = useState(false);
  // Drilldown state (richiesta utente 04/06/2026): clic su una barra
  // apre un modal con la lista delle opp vinte che compongono il totale
  // della cella (assicurazione × mese × diretto/indiretto).
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [drillCtx, setDrillCtx] = useState({ insurance: "", kind: "" });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data: resp } = await axios.get(`${API}/dashboard/insurance-revenue-by-month`, {
          params: { months: 24 },
          withCredentials: true,
        });
        if (!cancelled) setData(resp || { months: [], rows: [] });
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Errore caricamento dati");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Filtra le righe per il mese selezionato. Se il mese non è ancora nei
  // dati (es. range corto), barre = 0.
  const monthRows = useMemo(() => {
    if (!data.rows?.length) return [];
    const out = data.rows
      .map((r) => {
        const cell = r.by_month?.[selectedMonth] || { diretto: 0, indiretto: 0 };
        const dir = Number(cell.diretto || 0);
        const ind = Number(cell.indiretto || 0);
        return {
          insurance: r.insurance,
          diretto: dir,
          indiretto: ind,
          total: dir + ind,
        };
      })
      .filter((r) => r.total > 0)
      .sort((a, b) => b.total - a.total);
    return out;
  }, [data, selectedMonth]);

  const totals = useMemo(() => {
    const td = monthRows.reduce((a, r) => a + r.diretto, 0);
    const ti = monthRows.reduce((a, r) => a + r.indiretto, 0);
    return { diretto: td, indiretto: ti, total: td + ti };
  }, [monthRows]);

  const fmtEuro = (v) => {
    const n = Number(v || 0);
    if (!n) return "€ 0";
    try {
      return n.toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 });
    } catch {
      return `€ ${Math.round(n)}`;
    }
  };
  const fmtMonthLabel = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-").map(Number);
    const labels = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
    return `${labels[m - 1]} ${y}`;
  };

  const chartHeight = Math.max(280, monthRows.length * 38);

  // Click handler per le barre: apre il drilldown
  const handleBarClick = useCallback(async (entry, kind) => {
    if (!entry || !entry.insurance || !selectedMonth) return;
    setDrillCtx({ insurance: entry.insurance, kind });
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillData(null);
    try {
      const { data: resp } = await axios.get(
        `${API}/dashboard/insurance-revenue-by-month/drilldown`,
        {
          params: { insurance: entry.insurance, month: selectedMonth, kind },
          withCredentials: true,
        },
      );
      setDrillData(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento dettaglio");
    } finally {
      setDrillLoading(false);
    }
  }, [selectedMonth]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mt-4" data-testid="insurance-revenue-by-month">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Wallet className="w-4 h-4 text-emerald-500" />
          Incassato per assicurazione · <span className="text-emerald-700">{fmtMonthLabel(selectedMonth)}</span>
        </h2>
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-emerald-500" />
            <span className="text-slate-600 font-medium">Diretto</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-amber-500" />
            <span className="text-slate-600 font-medium">Indiretto</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      ) : monthRows.length === 0 ? (
        <div className="py-12 text-center text-slate-400 text-sm">
          Nessuna opportunità vinta in {fmtMonthLabel(selectedMonth)}
        </div>
      ) : (
        <>
          {/* KPI riassuntivi (3 card) */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 opacity-70">Diretto</div>
              <div className="text-xl font-bold text-emerald-700 tabular-nums">{fmtEuro(totals.diretto)}</div>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 opacity-70">Indiretto</div>
              <div className="text-xl font-bold text-amber-700 tabular-nums">{fmtEuro(totals.indiretto)}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 opacity-70">Totale</div>
              <div className="text-xl font-bold text-slate-800 tabular-nums">{fmtEuro(totals.total)}</div>
            </div>
          </div>

          {/* BarChart orizzontale: una riga per assicurazione, segmenti
              Diretto + Indiretto stacked */}
          <div style={{ width: "100%", height: chartHeight }}>
            <ResponsiveContainer>
              <BarChart
                data={monthRows}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
              >
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  tickFormatter={(v) => fmtEuro(v).replace("€", "€ ")}
                />
                <YAxis
                  type="category"
                  dataKey="insurance"
                  tick={{ fontSize: 11, fill: "#334155" }}
                  width={140}
                />
                <Tooltip
                  cursor={{ fill: "rgba(241, 245, 249, 0.6)" }}
                  contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                  formatter={(v, n) => [fmtEuro(v), n === "diretto" ? "Diretto" : "Indiretto"]}
                />
                <Bar
                  dataKey="diretto"
                  stackId="a"
                  fill="#10b981"
                  radius={[0, 0, 0, 0]}
                  cursor="pointer"
                  onClick={(e) => handleBarClick(e?.payload, "diretto")}
                />
                <Bar
                  dataKey="indiretto"
                  stackId="a"
                  fill="#f59e0b"
                  radius={[0, 6, 6, 0]}
                  cursor="pointer"
                  onClick={(e) => handleBarClick(e?.payload, "indiretto")}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 text-[11px] text-slate-400 text-center">
            Clicca su una barra per vedere le opportunità che compongono il totale.
          </p>
        </>
      )}

      {/* Drilldown dialog: lista opp che compongono la cella */}
      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent
          className="max-w-[92vw] sm:max-w-3xl max-h-[88vh] overflow-hidden flex flex-col p-0"
          data-testid="insurance-revenue-drilldown"
        >
          <DialogHeader className="px-5 py-4 border-b bg-slate-50">
            <DialogTitle className="text-base font-semibold text-slate-800">
              {drillCtx.kind === "indiretto" ? "Indiretto" : "Diretto"} ·{" "}
              <span className="text-emerald-700">{drillCtx.insurance || "—"}</span> ·{" "}
              <span className="text-slate-500">{fmtMonthLabel(selectedMonth)}</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 pt-1">
              {drillLoading
                ? "Caricamento dettaglio…"
                : drillData
                  ? `${drillData.count} opportunità · totale ${fmtEuro(drillData.total)}`
                  : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {drillLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
              </div>
            ) : !drillData || drillData.count === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">
                Nessuna opportunità trovata per questa cella.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500 bg-slate-50">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold">Data Won</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Paziente</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Pipeline</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Stage</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Medico</th>
                      <th className="text-right px-2 py-1.5 font-semibold">Valore</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Opp ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(drillData.opportunities || []).map((o) => {
                      const wd = o.won_at ? o.won_at.slice(0, 10) : "—";
                      return (
                        <tr key={o.opp_id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-2 py-1.5 tabular-nums text-slate-600 whitespace-nowrap">{wd}</td>
                          <td className="px-2 py-1.5 text-slate-800 font-medium truncate max-w-[180px]" title={o.contact_name}>{o.contact_name || "—"}</td>
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[200px]" title={o.pipeline_name}>{o.pipeline_name || "—"}</td>
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[140px]">{o.live_stage_name || "—"}</td>
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[140px]">{o.assigned_user_name || "—"}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">{fmtEuro(o.monetary_value)}</td>
                          <td className="px-2 py-1.5 font-mono text-[10px] text-slate-400 truncate max-w-[120px]" title={o.opp_id}>{o.opp_id}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 bg-slate-50">
                      <td colSpan={5} className="px-2 py-2 text-right font-bold text-slate-700">Totale</td>
                      <td className="px-2 py-2 text-right font-bold text-slate-900 tabular-nums">{fmtEuro(drillData.total)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// (NEW 02/06/2026 — richiesta utente)
// Toolbar comune ai due nuovi grafici: from-month / to-month / ricerca
// prestazioni (codice o nome). Stato locale per ogni card così l'utente
// può confrontare prestazioni diverse senza re-fetch dell'intera dashboard.
// ─────────────────────────────────────────────────────────────────────

function ChartFilters({ fromYm, toYm, q, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap items-end gap-2 mb-3 text-xs">
      <div className="flex flex-col">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Da</label>
        <input
          type="month"
          value={fromYm}
          onChange={(e) => onChange({ fromYm: e.target.value, toYm, q })}
          data-testid={`${testIdPrefix}-from`}
          className="h-9 border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400"
        />
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">A</label>
        <input
          type="month"
          value={toYm}
          onChange={(e) => onChange({ fromYm, toYm: e.target.value, q })}
          data-testid={`${testIdPrefix}-to`}
          className="h-9 border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400"
        />
      </div>
      <div className="flex flex-col flex-1 min-w-[180px]">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Cerca prestazione</label>
        <input
          type="text"
          value={q}
          placeholder="es. VIS, ABT, igiene..."
          onChange={(e) => onChange({ fromYm, toYm, q: e.target.value })}
          data-testid={`${testIdPrefix}-search`}
          className="h-9 border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400"
        />
      </div>
    </div>
  );
}

// Helper: ritorna [fromYm, toYm] di default (ultimi 6 mesi inclusivi).
function defaultRange() {
  const now = new Date();
  const to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const from = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  return [`${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, "0")}`, to];
}

// Palette per le serie top-N prestazioni nel grafico — estesa a 20
// colori distinti per evitare ambiguità visiva con i 20 codici top
// (richiesta utente 03/06/2026).
const TREATMENT_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#84cc16", "#0ea5e9", "#a855f7",
  "#f97316", "#14b8a6", "#eab308", "#dc2626", "#7c3aed",
  "#0891b2", "#db2777", "#65a30d", "#0284c7", "#9333ea",
];

// Debounce ricerca prestazione (300ms) per non bombardare l'endpoint a
// ogni tasto premuto dall'utente.
function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}


// ─────────────────────────────────────────────────────────────────────
// IndirettiDailyRevenueCard (richiesta utente 06/06/2026)
// Grafico a barre verticali con gli incassi giorno per giorno dei pazienti
// INDIRETTI per il mese selezionato. Mese pilotato dall'Header come per
// InsuranceRevenueByMonthCard.
// ─────────────────────────────────────────────────────────────────────
function IndirettiDailyRevenueCard({ selectedMonth }) {
  const [data, setData] = useState({ month: "", days: [], total: 0, total_count: 0 });
  const [loading, setLoading] = useState(false);
  // Drilldown state — apre il dialog con la lista opp del giorno cliccato
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [drillDay, setDrillDay] = useState("");

  useEffect(() => {
    if (!selectedMonth) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data: resp } = await axios.get(`${API}/dashboard/indiretti-daily-revenue`, {
          params: { month: selectedMonth },
          withCredentials: true,
        });
        if (!cancelled) setData(resp || { month: selectedMonth, days: [], total: 0, total_count: 0 });
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Errore caricamento incassi indiretti");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedMonth]);

  const fmtMonthLabel = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-").map(Number);
    const labels = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
    return `${labels[m - 1]} ${y}`;
  };
  const fmtEUR = (n) => {
    if (!n) return "€ 0";
    const abs = Math.abs(n);
    if (abs >= 1000) return `€ ${(n / 1000).toFixed(1)}k`;
    return `€ ${Math.round(n)}`;
  };

  // Trasforma i dati per il grafico: x = "1", "2", ..., y = total
  const chartData = useMemo(
    () => (data.days || []).map((d) => ({
      day: parseInt(d.day.split("-")[2], 10),
      total: d.total,
      count: d.count,
      fullDay: d.day,
    })),
    [data.days],
  );

  const hasData = (data.total || 0) > 0;

  // Click handler: apre il drilldown per il giorno cliccato
  const handleBarClick = useCallback(async (entry) => {
    if (!entry || !entry.fullDay) return;
    if (!entry.total || entry.total <= 0) return;  // ignora i giorni a 0
    setDrillDay(entry.fullDay);
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillData(null);
    try {
      const { data: resp } = await axios.get(
        `${API}/dashboard/indiretti-daily-revenue/drilldown`,
        { params: { day: entry.fullDay }, withCredentials: true },
      );
      setDrillData(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento dettaglio giorno");
    } finally {
      setDrillLoading(false);
    }
  }, []);

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mt-4"
      data-testid="indiretti-daily-revenue"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Users className="w-4 h-4 text-amber-600" />
          Incassi pazienti indiretti · <span className="text-amber-700">{fmtMonthLabel(selectedMonth)}</span>
        </h3>
        <div className="text-xs text-slate-500">
          {loading ? (
            <span className="flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Caricamento...</span>
          ) : (
            <span>
              Totale: <strong className="text-amber-700">€ {(data.total || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              {" · "}
              <strong>{data.total_count || 0}</strong> opp vinte
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        </div>
      ) : !hasData ? (
        <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
          Nessun incasso indiretto registrato in {fmtMonthLabel(selectedMonth)}
        </div>
      ) : (
        <>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 10, right: 16, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 11, fill: "#64748b" }}
              tickLine={false}
              axisLine={{ stroke: "#cbd5e1" }}
              label={{ value: "Giorno del mese", position: "insideBottom", offset: -2, style: { fontSize: 11, fill: "#94a3b8" } }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#64748b" }}
              tickLine={false}
              axisLine={{ stroke: "#cbd5e1" }}
              tickFormatter={fmtEUR}
            />
            <Tooltip
              formatter={(value, name) => {
                if (name === "total") return [`€ ${value.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, "Incasso"];
                return [value, name];
              }}
              labelFormatter={(label, payload) => {
                if (payload && payload[0]) return `Giorno ${label} · ${payload[0].payload.count} opp`;
                return `Giorno ${label}`;
              }}
              contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }}
            />
            <Bar
              dataKey="total"
              fill="#f59e0b"
              radius={[6, 6, 0, 0]}
              maxBarSize={32}
              cursor="pointer"
              onClick={(e) => handleBarClick(e?.payload)}
            />
          </BarChart>
        </ResponsiveContainer>
        <p className="mt-2 text-[11px] text-slate-400 text-center">
          Clicca su una barra per vedere le opportunità che compongono il totale di quel giorno.
        </p>
        </>
      )}

      {/* Drilldown dialog: lista opp del giorno selezionato */}
      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent
          className="max-w-[92vw] sm:max-w-3xl max-h-[88vh] overflow-hidden flex flex-col p-0"
          data-testid="indiretti-daily-revenue-drilldown"
        >
          <DialogHeader className="px-5 py-4 border-b bg-amber-50">
            <DialogTitle className="text-base font-semibold text-slate-800">
              Incassi indiretti · <span className="text-amber-700">{drillDay}</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 pt-1">
              {drillLoading
                ? "Caricamento dettaglio…"
                : drillData
                  ? `${drillData.count} opportunità · totale € ${(drillData.total || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {drillLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
              </div>
            ) : !drillData || drillData.count === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">
                Nessuna opportunità trovata per questo giorno.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="indiretti-drilldown-table">
                  <thead className="text-slate-500 bg-slate-50">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold">Data Won</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Paziente</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Pipeline</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Stage</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Medico</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Assicurazione</th>
                      <th className="text-right px-2 py-1.5 font-semibold">Valore</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Opp ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(drillData.opportunities || []).map((o) => {
                      const wd = o.won_at ? o.won_at.slice(0, 10) : "—";
                      return (
                        <tr key={o.opp_id} className="border-t border-slate-100 hover:bg-amber-50/40">
                          <td className="px-2 py-1.5 tabular-nums text-slate-600 whitespace-nowrap">{wd}</td>
                          <td className="px-2 py-1.5 text-slate-800 font-medium truncate max-w-[180px]" title={o.contact_name}>{o.contact_name || "—"}</td>
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[180px]" title={o.pipeline_name}>{o.pipeline_name || "—"}</td>
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[140px]">{o.live_stage_name || "—"}</td>
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[140px]">{o.assigned_user_name || "—"}</td>
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[140px]" title={o.insurance_name}>{o.insurance_name || "—"}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">
                            € {(o.monetary_value || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-2 py-1.5 font-mono text-[10px] text-slate-400 truncate max-w-[120px]" title={o.opp_id}>{o.opp_id}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 bg-amber-50/40">
                      <td colSpan={6} className="px-2 py-2 text-right font-bold text-slate-700">Totale</td>
                      <td className="px-2 py-2 text-right font-bold text-slate-900 tabular-nums">
                        € {(drillData.total || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function TreatmentsByMonthCard() {
  const [defFrom, defTo] = defaultRange();
  // `stages` ora è un Set<string> per supportare multi-selezione.
  const [filters, setFilters] = useState({
    fromYm: defFrom, toYm: defTo, q: "",
    stages: new Set(),
    kind: "",
  });
  const debouncedQ = useDebounced(filters.q, 300);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ months: [], rows: [] });
  const [error, setError] = useState("");
  const [stageGroups, setStageGroups] = useState([]); // [{name, stages: [...]}]
  const [stageMenuOpen, setStageMenuOpen] = useState(false);
  const stageMenuRef = useRef(null);

  // Carica una sola volta gli stage di TUTTE le pipeline attive GHL.
  // Filtriamo le pipeline Promemoria (richiesta utente 06/06/2026):
  // gli stage Promemoria sono mesi dell'anno, non workflow stages
  // significativi per "Prestazioni erogate per mese".
  useEffect(() => {
    cachedGet(`${API}/dashboard/pipeline-stages`)
      .then((res) => {
        const groups = res.data?.by_pipeline || [];
        const filtered = groups.filter((g) => !/promemoria/i.test(g.name || ""));
        setStageGroups(filtered);
      })
      .catch(() => setStageGroups([]));
  }, []);

  // Click outside per chiudere il menu multi-select.
  useEffect(() => {
    if (!stageMenuOpen) return;
    const onDocClick = (e) => {
      if (stageMenuRef.current && !stageMenuRef.current.contains(e.target)) {
        setStageMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [stageMenuOpen]);

  const stagesCsv = useMemo(() => Array.from(filters.stages).join(","), [filters.stages]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    const params = {
      from_ym: filters.fromYm,
      to_ym: filters.toYm,
    };
    if (debouncedQ) params.q = debouncedQ;
    if (stagesCsv) params.stages = stagesCsv;
    if (filters.kind) params.kind = filters.kind;
    axios.get(`${API}/dashboard/treatments-by-month`, { params, withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setData(res.data || { months: [], rows: [] });
      }).catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || "Errore nel caricamento prestazioni");
      }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters.fromYm, filters.toYm, filters.kind, stagesCsv, debouncedQ]);

  const toggleStage = (s) => {
    const next = new Set(filters.stages);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setFilters({ ...filters, stages: next });
  };
  const clearStages = () => setFilters({ ...filters, stages: new Set() });

  const stageButtonLabel = filters.stages.size === 0
    ? "Tutti gli stage"
    : filters.stages.size === 1
    ? Array.from(filters.stages)[0]
    : `${filters.stages.size} stage selezionati`;

  const rows = data.rows || [];
  const months = data.months || [];
  const totalCount = rows.reduce((acc, r) => acc + (r.total || 0), 0);
  const top10 = rows.slice(0, 10);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="treatments-by-month-card">
      <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-1">
        <BarChart3 className="w-4 h-4 text-sky-500" /> Prestazioni erogate per mese
      </h2>
      <p className="text-xs text-slate-500 mb-3">
        Quantità di singole prestazioni nei piani di cura non cancellati.
        Top 10 in evidenza + dettaglio per mese.
      </p>

      {/* Filtri */}
      <div className="flex flex-wrap items-end gap-2 mb-3 text-xs">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Da</label>
          <input
            type="month"
            value={filters.fromYm}
            onChange={(e) => setFilters({ ...filters, fromYm: e.target.value })}
            data-testid="treatments-filter-from"
            className="h-9 border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">A</label>
          <input
            type="month"
            value={filters.toYm}
            onChange={(e) => setFilters({ ...filters, toYm: e.target.value })}
            data-testid="treatments-filter-to"
            className="h-9 border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400"
          />
        </div>

        {/* Multi-select stage: pulsante che apre un popover con checkbox per
            pipeline. Permette di selezionare 1 o più stage. */}
        <div className="flex flex-col relative" ref={stageMenuRef}>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Stage</label>
          <button
            type="button"
            onClick={() => setStageMenuOpen((v) => !v)}
            data-testid="treatments-filter-stage-toggle"
            className="h-9 min-w-[200px] border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400 flex items-center justify-between gap-2"
          >
            <span className={filters.stages.size === 0 ? "text-slate-500" : "text-slate-700 font-medium"}>
              {stageButtonLabel}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          </button>
          {stageMenuOpen && (
            <div
              className="absolute top-full left-0 mt-1 z-30 w-[320px] max-h-[420px] overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl p-3"
              data-testid="treatments-filter-stage-menu"
            >
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  {filters.stages.size}/{stageGroups.reduce((a, g) => a + g.stages.length, 0)} selezionati
                </span>
                <button
                  type="button"
                  onClick={clearStages}
                  data-testid="treatments-filter-stage-clear"
                  disabled={filters.stages.size === 0}
                  className="text-[11px] text-slate-500 hover:text-rose-600 disabled:opacity-40 disabled:hover:text-slate-500"
                >
                  Pulisci
                </button>
              </div>
              {stageGroups.length === 0 ? (
                <div className="text-xs text-slate-400 py-3 text-center">Nessuna pipeline disponibile</div>
              ) : (
                stageGroups.map((g) => (
                  <div key={g.id} className="mb-3 last:mb-0">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">
                      {g.name}
                    </div>
                    <div className="space-y-1">
                      {g.stages.map((s) => {
                        const selected = filters.stages.has(s);
                        return (
                          <label
                            key={`${g.id}-${s}`}
                            className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-slate-50 cursor-pointer text-xs"
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleStage(s)}
                              data-testid={`treatments-stage-cb-${s}`}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className={selected ? "text-slate-800 font-medium" : "text-slate-600"}>{s}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Tipo</label>
          <select
            value={filters.kind}
            onChange={(e) => setFilters({ ...filters, kind: e.target.value })}
            data-testid="treatments-filter-kind"
            className="h-9 border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="">Tutti</option>
            <option value="direct">Solo Dirette</option>
            <option value="indirect">Solo Indirette</option>
          </select>
        </div>
        <div className="flex flex-col flex-1 min-w-[180px]">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Cerca</label>
          <input
            type="text"
            value={filters.q}
            placeholder="codice o nome..."
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            data-testid="treatments-filter-search"
            className="h-9 border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400"
          />
        </div>
      </div>

      {/* Chip riepilogo degli stage selezionati (più visibile del solo numero) */}
      {filters.stages.size > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="treatments-stage-chips">
          {Array.from(filters.stages).map((s) => (
            <span key={s} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 text-[11px] font-semibold px-2 py-0.5 rounded-full">
              {s}
              <button
                type="button"
                onClick={() => toggleStage(s)}
                data-testid={`treatments-stage-chip-remove-${s}`}
                className="hover:text-blue-900"
                aria-label={`Rimuovi ${s}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mb-3 text-xs">
        <span className="text-slate-500">{rows.length} codici · {totalCount} prestazioni totali</span>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700 mb-2">{error}</div>
      )}

      {rows.length === 0 || totalCount === 0 ? (
        <div className="py-12 text-center text-slate-400 text-sm" data-testid="treatments-empty">
          Nessuna prestazione nel periodo / filtro selezionato
        </div>
      ) : (
        <>
          {/* Top 10 box (2 righe da 5) — vista d'impatto */}
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
              Top 10 prestazioni più applicate
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2" data-testid="treatments-top10">
              {top10.map((r, idx) => (
                <div
                  key={r.code}
                  data-testid={`treatments-top-box-${r.code}`}
                  className="relative bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 rounded-xl p-3 hover:shadow-md transition-shadow"
                >
                  <div className="absolute top-1.5 right-2 text-[10px] font-bold text-sky-400">
                    #{idx + 1}
                  </div>
                  <div className="text-base font-bold text-slate-800 tabular-nums">{r.total}</div>
                  <div className="text-xs font-semibold text-sky-700 mt-0.5">{r.code}</div>
                  {r.name && (
                    <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-2 leading-tight" title={r.name}>
                      {r.name}
                    </div>
                  )}
                </div>
              ))}
              {/* Riempi box mancanti se < 10 risultati così la griglia resta 2×5 */}
              {Array.from({ length: Math.max(0, 10 - top10.length) }).map((_, i) => (
                <div key={`empty-${i}`} className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 p-3 min-h-[68px]" aria-hidden />
              ))}
            </div>
          </div>

          {/* Tabella dettagliata: 1 riga per codice + 1 colonna per mese */}
          <div className="overflow-x-auto" data-testid="treatments-table-wrap">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
              Dettaglio per mese
            </div>
            <table className="w-full text-xs" data-testid="treatments-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 font-bold text-slate-600">Cod.</th>
                  <th className="text-left py-2 px-2 font-bold text-slate-600">Prestazione</th>
                  {months.map((ym) => (
                    <th key={ym} className="text-right py-2 px-2 font-bold text-slate-600 tabular-nums whitespace-nowrap">
                      {formatYmShort(ym)}
                    </th>
                  ))}
                  <th className="text-right py-2 px-2 font-bold text-sky-700 tabular-nums">Tot</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr
                    key={r.code}
                    className={`border-b border-slate-100 hover:bg-slate-50/50 ${idx < 10 ? "bg-sky-50/30" : ""}`}
                    data-testid={`treatments-row-${r.code}`}
                  >
                    <td className="py-1.5 px-2 font-semibold text-sky-700">{r.code}</td>
                    <td className="py-1.5 px-2 text-slate-700 max-w-[280px] truncate" title={r.name}>{r.name || "—"}</td>
                    {months.map((ym) => {
                      const v = (r.by_month || {})[ym] || 0;
                      return (
                        <td key={ym} className={`text-right py-1.5 px-2 tabular-nums ${v === 0 ? "text-slate-300" : "text-slate-700"}`}>
                          {v || ""}
                        </td>
                      );
                    })}
                    <td className="text-right py-1.5 px-2 font-bold tabular-nums text-sky-700">{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function CompletedPlansByMonthCard() {
  const [defFrom, defTo] = defaultRange();
  // `stages` ora è un Set<string> per supportare multi-selezione (04/06/2026).
  const [filters, setFilters] = useState({
    fromYm: defFrom, toYm: defTo, q: "",
    stages: new Set(),
    kind: "",
  });
  const debouncedQ = useDebounced(filters.q, 300);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ months: [], rows: [] });
  const [error, setError] = useState("");
  const [stageGroups, setStageGroups] = useState([]); // [{name, stages: [...]}]
  const [stageMenuOpen, setStageMenuOpen] = useState(false);
  const stageMenuRef = useRef(null);

  // Carica una sola volta gli stage di TUTTE le pipeline attive GHL.
  // Filtriamo le pipeline Promemoria (richiesta utente 06/06/2026):
  // gli stage Promemoria sono mesi dell'anno, non sono workflow stages
  // significativi per il grafico "Piani di cura da autorizzati in poi".
  useEffect(() => {
    cachedGet(`${API}/dashboard/pipeline-stages`)
      .then((res) => {
        const groups = res.data?.by_pipeline || [];
        const filtered = groups.filter((g) => !/promemoria/i.test(g.name || ""));
        setStageGroups(filtered);
      })
      .catch(() => setStageGroups([]));
  }, []);

  // Click outside per chiudere il menu multi-select.
  useEffect(() => {
    if (!stageMenuOpen) return;
    const onDocClick = (e) => {
      if (stageMenuRef.current && !stageMenuRef.current.contains(e.target)) {
        setStageMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [stageMenuOpen]);

  const stagesCsv = useMemo(() => Array.from(filters.stages).join(","), [filters.stages]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    const params = {
      from_ym: filters.fromYm,
      to_ym: filters.toYm,
    };
    if (debouncedQ) params.q = debouncedQ;
    if (stagesCsv) params.stages = stagesCsv;
    if (filters.kind) params.kind = filters.kind;
    axios.get(`${API}/dashboard/completed-plans-by-month`, {
      params,
      withCredentials: true,
    }).then((res) => {
      if (cancelled) return;
      setData(res.data || { months: [], rows: [] });
    }).catch((err) => {
      if (cancelled) return;
      setError(err?.response?.data?.detail || "Errore nel caricamento piani completi");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters.fromYm, filters.toYm, filters.kind, stagesCsv, debouncedQ]);

  const toggleStage = (s) => {
    const next = new Set(filters.stages);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setFilters({ ...filters, stages: next });
  };
  const clearStages = () => setFilters({ ...filters, stages: new Set() });
  const stageButtonLabel = filters.stages.size === 0
    ? "Tutti gli stage"
    : filters.stages.size === 1
    ? Array.from(filters.stages)[0]
    : `${filters.stages.size} stage selezionati`;

  const chartData = useMemo(
    () => (data.rows || []).map((r) => ({ ...r, label: formatYmShort(r.ym) })),
    [data],
  );
  const totals = useMemo(() => {
    const eur = chartData.reduce((acc, r) => acc + (r.total_eur || 0), 0);
    const cnt = chartData.reduce((acc, r) => acc + (r.count || 0), 0);
    const wonEur = chartData.reduce((acc, r) => acc + (r.won_eur || 0), 0);
    const wonCnt = chartData.reduce((acc, r) => acc + (r.won_count || 0), 0);
    const lostEur = chartData.reduce((acc, r) => acc + (r.lost_eur || 0), 0);
    const lostCnt = chartData.reduce((acc, r) => acc + (r.lost_count || 0), 0);
    return { eur, cnt, avg: cnt > 0 ? eur / cnt : 0, wonEur, wonCnt, lostEur, lostCnt };
  }, [chartData]);

  const [forcingRefresh, setForcingRefresh] = useState(false);

  // Forza una RILETTURA completa da GHL di tutti gli stage delle opp
  // e ripopola `won_opportunities_archive`. Utile se l'utente sospetta
  // che la classificazione (vinti / persi / attivi) non sia aggiornata.
  // Chiama in sequenza i 2 endpoint admin già esistenti:
  //   1) /opp-history/trigger    → ri-legge open/won/lost/abandoned da GHL
  //   2) /won-archive/backfill   → ripopola l'archivio dei won
  // Al termine ricarica il grafico.
  const forceFullRefresh = useCallback(async () => {
    if (forcingRefresh) return;
    setForcingRefresh(true);
    try {
      toast.loading("Rilettura piani in corso da GHL…", { id: "force-refresh" });
      const r1 = await axios.post(`${API}/dashboard/opp-history/trigger`, null, { withCredentials: true });
      const r2 = await axios.post(`${API}/dashboard/won-archive/backfill`, null, { withCredentials: true });
      toast.success(
        `Aggiornato: ${r1.data?.transitions ?? 0} transizioni stage, ` +
          `${r2.data?.processed ?? 0} won archiviati (${r2.data?.total_in_archive ?? 0} totali).`,
        { id: "force-refresh", duration: 5000 },
      );
      // Forzo il refresh della card (riusa effect tramite cambio di filtri "no-op")
      setFilters((f) => ({ ...f }));
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Errore durante la rilettura forzata",
        { id: "force-refresh" },
      );
    } finally {
      setForcingRefresh(false);
    }
  }, [forcingRefresh]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="completed-plans-by-month-card">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Wallet className="w-4 h-4 text-emerald-500" /> Piani di cura · valore + numero
        </h2>
        <button
          type="button"
          onClick={forceFullRefresh}
          disabled={forcingRefresh}
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full border border-slate-200 bg-white text-[11px] font-semibold text-slate-600 hover:border-blue-300 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="completed-force-refresh-btn"
          title="Rilegge tutti gli stage da GHL e ripopola l'archivio dei vinti"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${forcingRefresh ? "animate-spin" : ""}`} />
          {forcingRefresh ? "Aggiornamento…" : "Forza aggiornamento"}
        </button>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap items-end gap-2 mb-3 text-xs">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Da</label>
          <input
            type="month"
            value={filters.fromYm}
            onChange={(e) => setFilters({ ...filters, fromYm: e.target.value })}
            data-testid="completed-filter-from"
            className="h-9 border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">A</label>
          <input
            type="month"
            value={filters.toYm}
            onChange={(e) => setFilters({ ...filters, toYm: e.target.value })}
            data-testid="completed-filter-to"
            className="h-9 border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400"
          />
        </div>

        {/* Multi-select stage */}
        <div className="flex flex-col relative" ref={stageMenuRef}>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Stage</label>
          <button
            type="button"
            onClick={() => setStageMenuOpen((v) => !v)}
            data-testid="completed-filter-stage-toggle"
            className="h-9 min-w-[200px] border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400 flex items-center justify-between gap-2"
          >
            <span className={filters.stages.size === 0 ? "text-slate-500" : "text-slate-700 font-medium"}>
              {stageButtonLabel}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          </button>
          {stageMenuOpen && (
            <div
              className="absolute top-full left-0 mt-1 z-30 w-[320px] max-h-[420px] overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl p-3"
              data-testid="completed-filter-stage-menu"
            >
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  {filters.stages.size}/{stageGroups.reduce((a, g) => a + g.stages.length, 0)} selezionati
                </span>
                <button
                  type="button"
                  onClick={clearStages}
                  data-testid="completed-filter-stage-clear"
                  disabled={filters.stages.size === 0}
                  className="text-[11px] text-slate-500 hover:text-rose-600 disabled:opacity-40 disabled:hover:text-slate-500"
                >
                  Pulisci
                </button>
              </div>
              {stageGroups.length === 0 ? (
                <div className="text-xs text-slate-400 py-3 text-center">Nessuna pipeline disponibile</div>
              ) : (
                stageGroups.map((g) => (
                  <div key={g.id} className="mb-3 last:mb-0">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">
                      {g.name}
                    </div>
                    <div className="space-y-1">
                      {g.stages.map((s) => {
                        const selected = filters.stages.has(s);
                        return (
                          <label
                            key={`${g.id}-${s}`}
                            className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-slate-50 cursor-pointer text-xs"
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleStage(s)}
                              data-testid={`completed-stage-cb-${s}`}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className={selected ? "text-slate-800 font-medium" : "text-slate-600"}>{s}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Tipo</label>
          <select
            value={filters.kind}
            onChange={(e) => setFilters({ ...filters, kind: e.target.value })}
            data-testid="completed-filter-kind"
            className="h-9 border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="">Tutti</option>
            <option value="direct">Solo Dirette</option>
            <option value="indirect">Solo Indirette</option>
          </select>
        </div>
        <div className="flex flex-col flex-1 min-w-[180px]">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Prestazione</label>
          <input
            type="text"
            value={filters.q}
            placeholder="codice o nome..."
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            data-testid="completed-filter-search"
            className="h-9 border border-slate-200 rounded-lg px-2 bg-white focus:outline-none focus:border-blue-400"
          />
        </div>
      </div>

      {/* Chip riepilogo stage selezionati */}
      {filters.stages.size > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="completed-stage-chips">
          {Array.from(filters.stages).map((s) => (
            <span key={s} className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-semibold px-2 py-0.5 rounded-full">
              {s}
              <button
                type="button"
                onClick={() => toggleStage(s)}
                data-testid={`completed-stage-chip-remove-${s}`}
                className="hover:text-emerald-900"
                aria-label={`Rimuovi ${s}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 opacity-70">Totale</div>
          <div className="text-lg font-bold text-emerald-700 tabular-nums" data-testid="completed-total-eur">{fmtEuro(totals.eur)}</div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 opacity-80">Vinti</div>
          <div className="text-lg font-bold text-amber-700 tabular-nums" data-testid="completed-won-eur">{fmtEuro(totals.wonEur)}</div>
          <div className="text-[10px] text-amber-700/70 mt-0.5" data-testid="completed-won-count">{totals.wonCnt} piani</div>
        </div>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 opacity-80">Persi / Abbandonati</div>
          <div className="text-lg font-bold text-rose-700 tabular-nums" data-testid="completed-lost-eur">{fmtEuro(totals.lostEur)}</div>
          <div className="text-[10px] text-rose-700/70 mt-0.5" data-testid="completed-lost-count">{totals.lostCnt} piani</div>
        </div>
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700 opacity-70">Piani</div>
          <div className="text-lg font-bold text-blue-700 tabular-nums" data-testid="completed-total-count">{totals.cnt}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 opacity-70">Valore medio</div>
          <div className="text-lg font-bold text-slate-800 tabular-nums" data-testid="completed-avg-eur">{fmtEuro(totals.avg)}</div>
        </div>
      </div>
      {loading && <div className="mb-2"><Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /></div>}
      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700 mb-2">{error}</div>
      )}
      {chartData.length === 0 || totals.cnt === 0 ? (
        <div className="py-12 text-center text-slate-400 text-sm" data-testid="completed-empty">
          Nessun piano completo nel periodo / filtro selezionato
        </div>
      ) : (
        <div style={{ width: "100%", height: 320 }} data-testid="completed-chart">
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 5, right: 12, left: 5, bottom: 5 }}>
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis
                yAxisId="eur"
                orientation="left"
                tick={{ fontSize: 11, fill: "#64748b" }}
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`}
                label={{ value: "€", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#94a3b8" } }}
              />
              <YAxis
                yAxisId="count"
                orientation="right"
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#64748b" }}
                label={{ value: "Piani", angle: 90, position: "insideRight", style: { fontSize: 10, fill: "#94a3b8" } }}
              />
              <Tooltip
                cursor={{ fill: "rgba(241, 245, 249, 0.6)" }}
                contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                formatter={(v, n) => {
                  if (n === "won_eur") return [fmtEuro(v), "Valore € vinti"];
                  if (n === "lost_eur") return [fmtEuro(v), "Valore € persi/abbandonati"];
                  if (n === "rest_eur") return [fmtEuro(v), "Valore € attivi (in corso)"];
                  if (n === "count") return [v, "Piani (totali)"];
                  return [v, n];
                }}
              />
              <Legend
                iconSize={10}
                wrapperStyle={{ fontSize: 11, paddingTop: 6 }}
                formatter={(value) => {
                  if (value === "won_eur") return "Valore € vinti";
                  if (value === "lost_eur") return "Valore € persi/abbandonati";
                  if (value === "rest_eur") return "Valore € attivi (in corso)";
                  if (value === "count") return "Piani totali";
                  return value;
                }}
              />
              {/* Stack: vinti (arancio) + persi (rosa/red) + attivi (verde).
                  Insieme formano il total_eur del mese. */}
              <Bar yAxisId="eur" dataKey="won_eur" stackId="eur" fill="#f59e0b" />
              <Bar yAxisId="eur" dataKey="lost_eur" stackId="eur" fill="#f43f5e" />
              <Bar yAxisId="eur" dataKey="rest_eur" stackId="eur" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Line yAxisId="count" type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// Formatter etichetta breve mese: "2026-04" → "apr '26".
function formatYmShort(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const labels = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
  const idx = Math.max(0, Math.min(11, parseInt(m, 10) - 1));
  return `${labels[idx]} '${String(y).slice(-2)}`;
}

// ────────────────────────────────────────────────────────────────────
// TreatmentsByInsuranceCard (richiesta utente 08/06/2026)
// Griglia di mini-grafici: per OGNI assicurazione le top 10 prestazioni
// più frequenti nel periodo, con l'importo € generato. Toggle:
//   - Periodo: mese selezionato in dashboard / ultimi 12 mesi
//   - Opportunità: solo VINTE / tutte (cancellate sempre escluse)
// ────────────────────────────────────────────────────────────────────
function TreatmentsByInsuranceCard({ selectedMonth }) {
  const [mode, setMode] = useState("month"); // "month" | "12m"
  // Mese locale della card (override del mese globale). Default = selectedMonth.
  const [localMonth, setLocalMonth] = useState(selectedMonth);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ months: [], insurances: [] });
  const [error, setError] = useState("");

  // Multi-select stage (richiesta utente 17/06/2026): stesso UX del
  // dropdown già esistente in "Prestazioni erogate per mese".
  const [selectedStages, setSelectedStages] = useState(() => new Set());
  const [stageGroups, setStageGroups] = useState([]);     // [{id, name, stages: [stage_name,...]}, ...]
  const [stageMenuOpen, setStageMenuOpen] = useState(false);
  const stageMenuRef = useRef(null);

  useEffect(() => {
    cachedGet(`${API}/dashboard/pipeline-stages`)
      .then((res) => {
        const groups = res.data?.by_pipeline || [];
        const filtered = groups.filter((g) => !/promemoria/i.test(g.name || ""));
        setStageGroups(filtered);
      })
      .catch(() => setStageGroups([]));
  }, []);

  useEffect(() => {
    if (!stageMenuOpen) return;
    const onDocClick = (e) => {
      if (stageMenuRef.current && !stageMenuRef.current.contains(e.target)) {
        setStageMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [stageMenuOpen]);

  const stagesCsv = useMemo(() => Array.from(selectedStages).join(","), [selectedStages]);

  // Quando cambia il mese globale aggiorno anche quello locale
  // (salvo l'utente abbia già scelto un mese diverso).
  useEffect(() => { setLocalMonth(selectedMonth); }, [selectedMonth]);

  // Costruisce 24 mesi (selezionabili dal dropdown) terminando al mese
  // selezionato a livello globale, ordine più recente → meno recente.
  const monthOptions = useMemo(() => {
    if (!selectedMonth) return [];
    const out = [];
    let [y, m] = selectedMonth.split("-").map(Number);
    for (let i = 0; i < 24; i += 1) {
      out.push(`${y}-${String(m).padStart(2, "0")}`);
      m -= 1;
      if (m === 0) { m = 12; y -= 1; }
    }
    return out;
  }, [selectedMonth]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    // 17/06/2026: include `stages` (CSV) quando l'utente ne ha selezionato
    // almeno uno. Vuoto → nessun filtro stage (= comportamento storico).
    const params = { month: localMonth, mode };
    if (stagesCsv) params.stages = stagesCsv;
    axios.get(`${API}/dashboard/treatments-by-insurance`, {
      params,
      withCredentials: true,
    }).then((res) => {
      if (cancelled) return;
      setData(res.data || { months: [], insurances: [] });
    }).catch((err) => {
      if (cancelled) return;
      setError(err?.response?.data?.detail || "Errore nel caricamento prestazioni per assicurazione");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [localMonth, mode, stagesCsv]);

  const toggleStage = (s) => {
    setSelectedStages((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };
  const clearStages = () => setSelectedStages(new Set());
  const stageButtonLabel = selectedStages.size === 0
    ? "Tutti gli stage"
    : selectedStages.size === 1
    ? Array.from(selectedStages)[0]
    : `${selectedStages.size} stage selezionati`;
  const totalStagesAvailable = stageGroups.reduce((a, g) => a + (g.stages?.length || 0), 0);

  const fmtEUR0 = (n) =>
    `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const insurances = data.insurances || [];
  const nMonths = (data.months || []).length || 1;
  const periodLabel = mode === "12m"
    ? `ultimi 12 mesi (${formatYmShort((data.months || [])[0])} → ${formatYmShort((data.months || [])[nMonths - 1])})`
    : formatYmShort(localMonth);

  const pill = (active) =>
    `px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
      active
        ? "bg-blue-600 border-blue-600 text-white"
        : "bg-white border-slate-200 text-slate-600 hover:border-blue-300"
    }`;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="treatments-by-insurance-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <Stethoscope className="w-4 h-4 text-violet-500" /> Prestazioni per Assicurazione
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Prestazioni più frequenti nei piani di cura ({periodLabel}) con importo € generato.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {mode === "month" && (
            <select
              className="h-8 rounded-full border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={localMonth || ""}
              onChange={(e) => setLocalMonth(e.target.value)}
              data-testid="tbi-month-select"
            >
              {monthOptions.map((ym) => (
                <option key={ym} value={ym}>{formatYmShort(ym)}</option>
              ))}
            </select>
          )}
          <div className="flex items-center gap-1">
            <button type="button" className={pill(mode === "month")} onClick={() => setMode("month")} data-testid="tbi-mode-month">
              Mese selezionato
            </button>
            <button type="button" className={pill(mode === "12m")} onClick={() => setMode("12m")} data-testid="tbi-mode-12m">
              Ultimi 12 mesi
            </button>
          </div>
          {/* 17/06/2026 — Multi-select stage (richiesta utente). Stesso
              UX usato in "Prestazioni erogate per mese". */}
          <div className="relative" ref={stageMenuRef}>
            <button
              type="button"
              onClick={() => setStageMenuOpen((v) => !v)}
              data-testid="tbi-stage-toggle"
              className="h-8 rounded-full border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 flex items-center gap-2 min-w-[150px] justify-between"
            >
              <span className={selectedStages.size === 0 ? "text-slate-500" : "text-slate-700"}>
                {stageButtonLabel}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            </button>
            {stageMenuOpen && (
              <div
                className="absolute top-full right-0 mt-1 z-30 w-[320px] max-h-[420px] overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl p-3"
                data-testid="tbi-stage-menu"
              >
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    {selectedStages.size}/{totalStagesAvailable} selezionati
                  </span>
                  <button
                    type="button"
                    onClick={clearStages}
                    data-testid="tbi-stage-clear"
                    disabled={selectedStages.size === 0}
                    className="text-[11px] text-slate-500 hover:text-rose-600 disabled:opacity-40 disabled:hover:text-slate-500"
                  >
                    Pulisci
                  </button>
                </div>
                {stageGroups.length === 0 ? (
                  <div className="text-xs text-slate-400 py-3 text-center">Nessuna pipeline disponibile</div>
                ) : (
                  stageGroups.map((g) => (
                    <div key={g.id} className="mb-3 last:mb-0">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{g.name}</div>
                      <div className="space-y-1">
                        {(g.stages || []).map((s) => {
                          const selected = selectedStages.has(s);
                          return (
                            <label
                              key={`${g.id}-${s}`}
                              className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-slate-50 cursor-pointer text-xs"
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleStage(s)}
                                data-testid={`tbi-stage-cb-${s}`}
                                className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className={selected ? "text-slate-800 font-medium" : "text-slate-600"}>{s}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Chip riepilogo stage selezionati (sotto il header). */}
      {selectedStages.size > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="tbi-stage-chips">
          {Array.from(selectedStages).map((s) => (
            <span
              key={s}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-violet-50 text-violet-700 border border-violet-200"
            >
              {s}
              <button
                type="button"
                onClick={() => toggleStage(s)}
                className="text-violet-500 hover:text-violet-800 leading-none"
                aria-label={`Rimuovi ${s}`}
                title="Rimuovi"
              >×</button>
            </span>
          ))}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-400 my-3">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Caricamento…
        </div>
      )}
      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700 my-2">{error}</div>
      )}

      {!loading && !error && insurances.length === 0 && (
        <div className="py-12 text-center text-slate-400 text-sm" data-testid="tbi-empty">
          Nessuna prestazione nel periodo selezionato
        </div>
      )}

      {insurances.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4" data-testid="tbi-grid">
          {insurances.map((ins) => {
            const maxCount = Math.max(...ins.items.map((i) => i.count), 1);
            return (
              <div
                key={ins.name}
                className="border border-slate-200 rounded-xl p-4 bg-gradient-to-br from-slate-50/60 to-white hover:shadow-md transition-shadow"
                data-testid={`tbi-card-${ins.name}`}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-slate-800 truncate" title={ins.name}>{ins.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {ins.opp_count} {ins.opp_count === 1 ? "piano" : "piani"} · {ins.total_count} prestazioni
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-emerald-600 tabular-nums">{fmtEUR0(ins.total_amount)}</div>
                    {mode === "12m" && (
                      <div className="text-[10px] text-slate-400 tabular-nums">
                        ~{fmtEUR0(ins.total_amount / nMonths)}/mese
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  {ins.items.map((it, idx) => (
                    <div key={it.code} className="relative rounded-lg overflow-hidden" data-testid={`tbi-item-${ins.name}-${it.code}`}>
                      <div
                        className="absolute inset-y-0 left-0 bg-violet-100/80"
                        style={{ width: `${Math.max(4, (it.count / maxCount) * 100)}%` }}
                        aria-hidden
                      />
                      <div className="relative flex items-center justify-between gap-2 px-2 py-1 text-xs">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-[10px] font-bold text-violet-400 w-5 shrink-0">#{idx + 1}</span>
                          <span className="font-semibold text-violet-700 shrink-0">{it.code}</span>
                          <span className="text-slate-500 truncate" title={it.name}>{it.name}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 tabular-nums">
                          <span className="font-bold text-slate-700">{it.count}×</span>
                          <span className="text-emerald-600 font-semibold w-[72px] text-right">{fmtEUR0(it.amount)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {ins.others_distinct > 0 && (
                    <div
                      className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs border-t border-dashed border-slate-200 mt-1 text-slate-500 italic"
                      data-testid={`tbi-others-${ins.name}`}
                      title={`Le ${ins.others_distinct} prestazioni successive sono raggruppate qui per concisione`}
                    >
                      <span className="truncate">
                        + altre {ins.others_distinct} prestazioni
                      </span>
                      <div className="flex items-center gap-2 shrink-0 tabular-nums">
                        <span className="font-semibold text-slate-600">{ins.others_count}×</span>
                        <span className="text-emerald-700/70 font-semibold w-[72px] text-right">{fmtEUR0(ins.others_amount)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

